import type { AccountListItem } from '../api-client';

export type PickerFilter = 'liability_credit' | 'asset_spendable' | 'asset_receivable' | 'all';

export interface AccountPickerCallbacks {
  onPick(accountId: string): void;
  onCancel(): void;
}

export interface AccountPickerOptions {
  filter: PickerFilter;
  selectedId?: string | null;
  title?: string;
}

/**
 * Modal picker. Filter rules:
 *   - liability_credit: kind='liability' AND type='credit' (Plan 4 — pick a card to pay TO)
 *   - asset_spendable:  kind='asset' AND type IN ('debit','multi_currency_wallet') (Plan 6 — pick a funding source)
 *   - all:              no filter (escape hatch)
 */
export function renderAccountPicker(
  root: HTMLElement,
  accounts: AccountListItem[],
  cb: AccountPickerCallbacks,
  opts: AccountPickerOptions,
): void {
  const eligible = applyFilter(accounts, opts.filter)
    .sort((a, b) => a.bank.localeCompare(b.bank));

  const title = opts.title ?? defaultTitleFor(opts.filter);

  root.innerHTML = `
    <div class="picker-backdrop" id="picker-backdrop">
      <div class="picker">
        <h3>${escapeHtml(title)}</h3>
        <ul class="picker-list">
          ${eligible.map((a) => `
            <li class="picker-row${a.id === opts.selectedId ? ' picker-selected' : ''}" data-id="${a.id}">
              <span>${escapeHtml(a.name)}</span>
              ${a.id === opts.selectedId ? '<span class="picker-tick">✓</span>' : ''}
            </li>
          `).join('')}
        </ul>
        <button class="picker-cancel ghost" id="picker-cancel">Cancel</button>
      </div>
    </div>
  `;

  for (const li of Array.from(root.querySelectorAll<HTMLElement>('.picker-row'))) {
    li.addEventListener('click', () => {
      const id = li.getAttribute('data-id');
      if (id) cb.onPick(id);
    });
  }
  root.querySelector<HTMLElement>('#picker-cancel')!.addEventListener('click', () => cb.onCancel());
  root.querySelector<HTMLElement>('#picker-backdrop')!.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) cb.onCancel();
  });
}

function applyFilter(accounts: AccountListItem[], filter: PickerFilter): AccountListItem[] {
  switch (filter) {
    case 'liability_credit':
      return accounts.filter((a) => a.kind === 'liability' && a.type === 'credit');
    case 'asset_spendable':
      return accounts.filter((a) => a.kind === 'asset' && (a.type === 'debit' || a.type === 'multi_currency_wallet'));
    case 'asset_receivable':
      return accounts.filter((a) => a.kind === 'asset');
    case 'all':
      return accounts;
  }
}

function defaultTitleFor(filter: PickerFilter): string {
  switch (filter) {
    case 'liability_credit': return 'Pick a credit card';
    case 'asset_spendable':  return 'Pick a funding account';
    case 'asset_receivable': return 'Received into';
    case 'all':              return 'Pick an account';
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
